import type { Obligation } from '@mfos/shared';
import type { ReactNode } from 'react';
import { StatusBadge } from '../../components/Badge';
import { formatDateTime, humanize } from '../../lib/format';
import { phrase, useI18n } from '../../lib/i18n/runtime';
import { describeDueRule } from './due-rule';

/** Founder responsibilities; due times are shown in the workspace time zone. */
export function ObligationList({
  obligations,
  timeZone,
  renderActions,
}: {
  obligations: readonly Obligation[];
  timeZone: string;
  renderActions?: (obligation: Obligation) => ReactNode;
}) {
  const { t } = useI18n();
  if (obligations.length === 0) {
    return <p className="meta">{t('noResponsibilities')}</p>;
  }
  return (
    <ul className="item-list">
      {obligations.map((obligation) => (
        <li key={obligation.id} className="item">
          <div className="item__header">
            <h3 className="item__title">{obligation.commitment}</h3>
            <StatusBadge status={obligation.status} />
          </div>
          <dl className="inline-facts">
            <div>
              <dt>{phrase('Area')}</dt>
              <dd>{humanize(obligation.category)}</dd>
            </div>
            <div>
              <dt>{phrase('Cadence')}</dt>
              <dd>{describeDueRule(obligation.dueRule)}</dd>
            </div>
            <div>
              <dt>{phrase('Next due')}</dt>
              <dd>
                {obligation.nextDueAt !== null ? (
                  <time dateTime={obligation.nextDueAt}>
                    {formatDateTime(obligation.nextDueAt, timeZone)}
                  </time>
                ) : (
                  'Not scheduled'
                )}
              </dd>
            </div>
            <div>
              <dt>{phrase('Evidence')}</dt>
              <dd>
                {humanize(obligation.evidenceRequirement)}
                {obligation.evidenceDescription !== null
                  ? ` — ${obligation.evidenceDescription}`
                  : ''}
              </dd>
            </div>
            {obligation.gracePeriodHours !== null && (
              <div>
                <dt>{phrase('Grace period')}</dt>
                <dd>{obligation.gracePeriodHours} h</dd>
              </div>
            )}
          </dl>
          <p className="meta">
            Times in {timeZone}. Assigned by {obligation.assignedBy}.
          </p>
          {renderActions !== undefined && (
            <div className="item__actions">{renderActions(obligation)}</div>
          )}
        </li>
      ))}
    </ul>
  );
}
